import React from 'react';
import { Icon } from 'semantic-ui-react';
import styled from 'styled-components';

export const UPGRADE_BANNER_TIERS = {
  Insight: 'Insight',
  Improve: 'Improve',
  Lead: 'Lead',
  Enterprise: 'Enterprise',
} as const;

export type UpgradeBannerTier = (typeof UPGRADE_BANNER_TIERS)[keyof typeof UPGRADE_BANNER_TIERS];

export type UpgradeBannerProps = {
  heading: string;
  tier: UpgradeBannerTier;
  iconName?: string;
};

const TierLabel = styled.strong`
  font-weight: 700;
  color: #0f172a;
`;

const UpgradeBanner = ({
  heading,
  tier,
  iconName = 'rocket',
}: UpgradeBannerProps) => (
  <BannerCard>
    <BannerIcon>
      <Icon name={iconName as any} size="huge" color="blue" />
    </BannerIcon>
    <BannerContent>
      <BannerHeading>{heading}</BannerHeading>
      <BannerText>
        Sharing content with team members is available at the <TierLabel>{tier}</TierLabel> tier of nuLogic.
      </BannerText>
      <BannerSubtext>
        Contact <NuwaveLink href="https://www.nuwaveco.com/" target="_blank" rel="noopener noreferrer">nuwave</NuwaveLink> for more information and a demo of the powerful features available to you and your team.
      </BannerSubtext>
      <BannerCallout href="https://www.nuwaveco.com/" target="_blank" rel="noopener noreferrer">
        Unlock team sharing, collaboration, and better engagement across your organization.
      </BannerCallout>
    </BannerContent>
  </BannerCard>
);

const BannerCard = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 24px;
  align-items: center;
  padding: 28px;
  border-radius: 20px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.14), rgba(14, 165, 233, 0.06));
  border: 1px solid rgba(96, 165, 250, 0.2);
  color: #0f172a;
  margin: 0 12px;
`;

const BannerIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 84px;
  height: 84px;
  border-radius: 24px;
  background: rgba(59, 130, 246, 0.12);
`;

const BannerContent = styled.div`
  display: grid;
  gap: 10px;
`;

const BannerHeading = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
`;

const BannerText = styled.div`
  font-size: 1rem;
  color: #334155;
  line-height: 1.6;
`;

const BannerSubtext = styled.div`
  font-size: 0.95rem;
  color: #475569;
  line-height: 1.6;
`;

const NuwaveLink = styled.a`
  color: #334155;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }
`;

const BannerCallout = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 14px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  transition: text-decoration-color 0.2s ease;

  &:hover {
    text-decoration: underline;
  }
`;

export default UpgradeBanner;
